import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Users, TrendingUp, Sparkles, Shield, Target } from 'lucide-react';

const Home = () => {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 gradient-hero" />
        <div className="absolute inset-0 pattern-bg" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-36">
          <div className="max-w-4xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border shadow-soft mb-8">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-muted-foreground">Trusted by 1,000+ Business Owners</span>
            </div>
            
            <h1 className="font-display text-5xl lg:text-6xl xl:text-7xl text-foreground mb-8 leading-[1.1]">
              Expert Guidance for{' '}
              <span className="text-gradient">Business Transitions</span>
            </h1>
            
            <p className="text-lg lg:text-xl text-muted-foreground mb-12 leading-relaxed max-w-2xl">
              First Choice Business Brokers provides trusted advisory services for business owners 
              preparing to sell, grow, or exit. Access our library of practical resources.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/newsletters"
                className="btn-primary px-8 py-4 rounded-xl font-semibold inline-flex items-center justify-center gap-3 text-base"
              >
                Browse Newsletter Library
                <ArrowRight className="h-5 w-5" />
              </Link>
              <a
                href="https://calendly.com/montie-barnesfcbb/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary px-8 py-4 rounded-xl font-semibold inline-flex items-center justify-center text-base"
              >
                Schedule a Consultation
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative border-t border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-gradient mb-2">25+</div>
              <div className="text-sm text-muted-foreground">Years Experience</div>
            </div>
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-gradient mb-2">5,000+</div>
              <div className="text-sm text-muted-foreground">Deals Closed</div>
            </div>
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-gradient mb-2">$2B+</div>
              <div className="text-sm text-muted-foreground">Transaction Value</div>
            </div>
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-gradient mb-2">98%</div>
              <div className="text-sm text-muted-foreground">Client Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="relative bg-background">
        <div className="absolute inset-0 pattern-bg opacity-50" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl lg:text-4xl text-foreground mb-4">
              Why Business Owners Trust Us
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our comprehensive approach ensures you have everything you need for a successful transition.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            <div className="feature-card p-8 rounded-2xl group">
              <div className="icon-container w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <BookOpen className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-display text-xl text-foreground mb-4">
                Practical Knowledge
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Curated insights distilled from thousands of successful business transitions. 
                Real-world guidance, not theoretical advice.
              </p>
            </div>

            <div className="feature-card p-8 rounded-2xl group">
              <div className="icon-container w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <Shield className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-display text-xl text-foreground mb-4">
                Owner-Focused
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Written specifically for business owners by professionals who understand 
                the unique challenges of selling or exiting a business.
              </p>
            </div>

            <div className="feature-card p-8 rounded-2xl group">
              <div className="icon-container w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <Target className="h-7 w-7 text-primary" />
              </div>
              <h3 className="font-display text-xl text-foreground mb-4">
                Actionable Guidance
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Each newsletter provides clear, actionable steps you can implement 
                immediately to improve your position.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[hsl(350,50%,25%)]" />
        <div className="absolute inset-0 pattern-bg opacity-10" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-3xl lg:text-4xl text-white mb-6">
              Ready to explore your options?
            </h2>
            <p className="text-white/80 mb-10 leading-relaxed text-lg">
              Our team of experienced brokers is ready to provide confidential guidance 
              tailored to your specific situation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-[hsl(350,50%,30%)] px-8 py-4 rounded-xl font-semibold inline-flex items-center justify-center gap-2 hover:bg-white/90 transition-smooth shadow-soft"
              >
                Get in Touch
                <ArrowRight className="h-5 w-5" />
              </Link>
              <a
                href="https://calendly.com/montie-barnesfcbb/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/30 text-white px-8 py-4 rounded-xl font-semibold inline-flex items-center justify-center hover:bg-white/10 transition-smooth"
              >
                Schedule a Consultation
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
